import React, { createContext, useState, useContext, ReactNode, useEffect, useRef, useCallback } from 'react';
import { Page } from '../types';
import {
  getRouteStateFromPathname,
  getSlugFromPage,
  isAuthPath,
  isSuperadminPage,
  normalizeStoredPage,
  resolveAppPathChange,
} from '../utils/routeUtils';

interface AppContextType {
  currentPage: Page;
  setCurrentPage: (page: Page) => void;
  goBack: () => void;
  canGoBack: boolean;
  superadminOrgId: string | null;
  openSuperadminOrg: (orgId: string) => void;
  closeSuperadminOrg: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

/** Chave legada — também limpa no AuthContext no logout. */
export const APP_PAGE_STORAGE_KEY = 'impulse_crm_current_page';
const STORAGE_KEY = APP_PAGE_STORAGE_KEY;

const FUNNEL_PAGES: Page[] = ['create-funnel', 'edit-funnel'];

/** Páginas raiz / seções onde o botão voltar global não aparece. */
const MAIN_PAGES: Page[] = [
  'dashboard',
  'chat',
  'contacts',
  'kanban',
  'tasks',
  'products',
  'subscription',
  'onboarding',
  'profile',
  'monitoring',
  'whatsapp',
  'team',
  'billing',
  'tracking',
  'connections',
  'settings',
  'settings-tags',
  'settings-quick-replies',
  'settings-pix',
  'settings-loss-reasons',
  'settings-origins',
  'settings-webhooks',
  'settings-auto-movement',
  'follow-up',
  'support',
  'superadmin',
  'superadmin-organizations',
  'superadmin-finance',
  'superadmin-invoices',
  'superadmin-whatsapp',
  'superadmin-integrations',
  'superadmin-communications',
  'superadmin-tickets',
  'superadmin-comms-whatsapp',
];

function applyPathChange(change: { path: string; mode: 'push' | 'replace' }) {
  if (change.mode === 'replace') {
    window.history.replaceState(null, '', change.path);
    return;
  }
  window.history.pushState(null, '', change.path);
}

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const initialRoute = getRouteStateFromPathname(window.location.pathname);
  const [currentPage, setCurrentPageState] = useState<Page>(initialRoute.page);
  const [superadminOrgId, setSuperadminOrgId] = useState<string | null>(initialRoute.superadminOrgId);
  const historyRef = useRef<Page[]>([currentPage]);

  const syncRouteFromPathname = useCallback(() => {
    const route = getRouteStateFromPathname(window.location.pathname);
    setCurrentPageState(route.page);
    setSuperadminOrgId(route.superadminOrgId);

    const lastPage = historyRef.current[historyRef.current.length - 1];
    if (lastPage !== route.page) {
      historyRef.current.push(route.page);
    }
  }, []);

  useEffect(() => {
    const handlePopState = () => syncRouteFromPathname();
    const handleExternalNavigate = () => syncRouteFromPathname();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleExternalNavigate();
      }
    };

    window.addEventListener('popstate', handlePopState);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleExternalNavigate);

    const pathname = window.location.pathname;
    const isAuthRoute =
      isAuthPath(pathname) ||
      window.location.search.includes('invite') ||
      window.location.search.includes('token=');

    const cleanPath = pathname.replace(/^\//, '').replace(/\/$/, '');
    if (cleanPath === 'superadmin-usuarios') {
      const listPath = `/${getSlugFromPage('superadmin-organizations')}`;
      window.history.replaceState(null, '', listPath);
      setCurrentPageState('superadmin-organizations');
      setSuperadminOrgId(null);
    }

    if (cleanPath === 'superadmin-faturas') {
      const financePath = `/${getSlugFromPage('superadmin-finance')}`;
      window.history.replaceState(null, '', financePath);
      setCurrentPageState('superadmin-finance');
      setSuperadminOrgId(null);
    }

    // Na raiz: restaura última página salva (URL + state React).
    // Superadmin pages are restored only after useSuperadminPageGuard confirms role.
    if (!isAuthRoute && (pathname === '/' || pathname === '')) {
      const savedPage = localStorage.getItem(STORAGE_KEY);
      if (savedPage) {
        let page = normalizeStoredPage(savedPage);
        if (isSuperadminPage(page)) {
          page = 'dashboard';
        }
        window.history.replaceState(null, '', `/${getSlugFromPage(page)}`);
        setCurrentPageState(page);
        setSuperadminOrgId(null);
      }
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleExternalNavigate);
    };
  }, [syncRouteFromPathname]);

  const updatePathname = useCallback((page: Page, orgId: string | null) => {
    const change = resolveAppPathChange(window.location.pathname, page, { superadminOrgId: orgId });
    if (!change) return;
    applyPathChange(change);
  }, []);

  const setCurrentPage = useCallback((page: Page) => {
    page = normalizeStoredPage(page);

    const nextOrgId = page === 'superadmin-organization-detail' ? superadminOrgId : null;

    if (page === currentPage && page !== 'superadmin-organization-detail') {
      return;
    }

    if (page !== currentPage) {
      historyRef.current.push(page);
      if (historyRef.current.length > 10) {
        historyRef.current.shift();
      }
    }

    if (page !== 'superadmin-organization-detail') {
      setSuperadminOrgId(null);
    }

    updatePathname(page, nextOrgId);
    setCurrentPageState(page);
    // Onboarding is a gate, not a "last page" — keep prior slug for reload restore.
    if (page === 'onboarding') return;
    try {
      localStorage.setItem(STORAGE_KEY, page);
    } catch (error) {
      console.error('Error saving page to localStorage:', error);
    }
  }, [currentPage, superadminOrgId, updatePathname]);

  const openSuperadminOrg = useCallback((orgId: string) => {
    setSuperadminOrgId(orgId);

    if (currentPage !== 'superadmin-organization-detail') {
      historyRef.current.push('superadmin-organization-detail');
      if (historyRef.current.length > 10) {
        historyRef.current.shift();
      }
    }

    updatePathname('superadmin-organization-detail', orgId);
    setCurrentPageState('superadmin-organization-detail');
    try {
      localStorage.setItem(STORAGE_KEY, 'superadmin-organization-detail');
    } catch (error) {
      console.error('Error saving page to localStorage:', error);
    }
  }, [currentPage, updatePathname]);

  const closeSuperadminOrg = useCallback(() => {
    setSuperadminOrgId(null);
    setCurrentPage('superadmin-organizations');
  }, [setCurrentPage]);

  const goBack = useCallback(() => {
    if (FUNNEL_PAGES.includes(currentPage)) {
      setCurrentPage('kanban');
      return;
    }

    if (currentPage === 'superadmin-organization-detail') {
      closeSuperadminOrg();
      return;
    }

    if (historyRef.current.length > 1) {
      historyRef.current.pop();
      const previousPage = historyRef.current[historyRef.current.length - 1];
      setCurrentPage(previousPage);
      return;
    }

    window.history.back();
  }, [closeSuperadminOrg, currentPage, setCurrentPage]);

  const canGoBack =
    FUNNEL_PAGES.includes(currentPage) ||
    currentPage === 'superadmin-organization-detail' ||
    (historyRef.current.length > 1 && !MAIN_PAGES.includes(currentPage));

  const contextValue = React.useMemo(
    () => ({
      currentPage,
      setCurrentPage,
      goBack,
      canGoBack,
      superadminOrgId,
      openSuperadminOrg,
      closeSuperadminOrg,
    }),
    [currentPage, setCurrentPage, goBack, canGoBack, superadminOrgId, openSuperadminOrg, closeSuperadminOrg],
  );

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
